/**
 * FONTE DA VERDADE DA MINUTA — snapshot + hash determinístico do que
 * REALMENTE entra na Minuta (Recommendations por id estável, texto, ZR,
 * classificação, disciplina; condição do equipamento; Vida Residual; dados
 * documentais exibidos). Nada transitório entra: updatedAt, ordem acidental,
 * timestamps de leitura/renderização, propriedades de UI.
 *
 * "ATUALIZAÇÃO DA MINUTA NECESSÁRIA" aparece SOMENTE quando
 * currentSourceHash !== minuta.lastSourceHash.
 */

export interface MinutaSourceRecommendation {
  id: string;
  text: string;
  zr: string;
  criticality: string;
  discipline: string;
  componentName: string;
  location: string;
  dimensions: string;
  standard: string;
  executionType: string;
  status: string;
}

export interface MinutaSourceAssessment {
  equipmentCondition: string;
  equipmentResult: string;
  residualLifeMonths: number | null;
  residualLifeOrigin: string;
}

export interface MinutaSourceDocumentary {
  clientName: string;
  projectName: string;
  unitArea: string;
  osNumber: string;
  tag: string;
  equipmentType: string;
  fabricationStandard: string;
  inspectorName: string;
}

export interface MinutaSourceSnapshot {
  reportId: string;
  reportRevision: string;
  recommendations: MinutaSourceRecommendation[];
  assessment: MinutaSourceAssessment | null;
  documentary: MinutaSourceDocumentary;
  sourceHash: string;
  generatedAt: string;
}

const norm = (v: unknown): string => (v === null || v === undefined ? "" : String(v)).replace(/\r\n/g, "\n").trim();

/** Recommendation viva → linha da fonte (só os campos que afetam a Minuta). */
export function sourceRecommendationOf(rec: any): MinutaSourceRecommendation {
  return {
    id: norm(rec.id),
    text: norm(rec.description || rec.scope || rec.fullDescription || ""),
    zr: norm(rec.zr),
    criticality: norm(rec.criticality).toUpperCase(),
    discipline: norm(rec.discipline || "CALDEIRARIA").toUpperCase(),
    componentName: norm(rec.componentName || rec.component),
    location: norm(rec.location),
    dimensions: norm(rec.dimensions || rec.dimension),
    standard: norm(rec.standard || rec.norm),
    // P0-02/P0-04: domínio usa `executeIn` ('ROUTINE'|'SHUTDOWN'); snapshot/legacy usam `executionType` ou `execution`.
    executionType: norm(rec.executionType || rec.execution || (rec.executeIn === 'SHUTDOWN' ? 'PARADA' : rec.executeIn === 'ROUTINE' ? 'ROTINA' : rec.executeIn)).toUpperCase(),
    status: norm(rec.status).toUpperCase()
  };
}

/** Recommendation como está gravada no modelo da Minuta → linha da fonte (base para Minutas anteriores ao hash). */
export function sourceRecommendationFromMinutaModel(rec: any): MinutaSourceRecommendation {
  return {
    id: norm(rec.recommendationId || rec.id),
    text: norm(rec.fullDescription || [rec.title, rec.body].filter(Boolean).join("\n")),
    zr: norm(rec.zr),
    criticality: norm(rec.criticality).toUpperCase(),
    discipline: norm(rec.discipline).toUpperCase(),
    componentName: norm(rec.componentName),
    location: norm(rec.location),
    dimensions: norm(rec.dimensions),
    standard: norm(rec.standard),
    executionType: norm(rec.executionType).toUpperCase(),
    status: norm(rec.status).toUpperCase()
  };
}

/** Canonicaliza (chaves ordenadas, recomendações por id) — a mesma entrada produz sempre a mesma string. */
export function canonicalMinutaSource(input: Omit<MinutaSourceSnapshot, "sourceHash" | "generatedAt">): string {
  const recs = [...input.recommendations].map(r => ({ ...r })).sort((a, b) => a.id.localeCompare(b.id));
  const sortKeys = (value: any): any => {
    if (Array.isArray(value)) return value.map(sortKeys);
    if (value && typeof value === "object") return Object.keys(value).sort().reduce((acc: any, k) => { acc[k] = sortKeys(value[k]); return acc; }, {});
    return value;
  };
  return JSON.stringify(sortKeys({ recommendations: recs, assessment: input.assessment, documentary: input.documentary }));
}

/** FNV-1a 64 bits em hex — determinístico, síncrono, sem dependências. */
export function fnv1a64(text: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const bytes = new TextEncoder().encode(text);
  for (const b of bytes) {
    hash ^= BigInt(b);
    hash = (hash * prime) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, "0");
}

export function minutaSourceHash(input: Omit<MinutaSourceSnapshot, "sourceHash" | "generatedAt">): string {
  return fnv1a64(canonicalMinutaSource(input));
}

export function buildMinutaSource(input: { reportId: string; reportRevision: string; recommendations: any[]; assessment: MinutaSourceAssessment | null; documentary: MinutaSourceDocumentary; fromMinutaModel?: boolean }, generatedAt = new Date().toISOString()): MinutaSourceSnapshot {
  const recommendations = input.recommendations.map(r => (input.fromMinutaModel ? sourceRecommendationFromMinutaModel(r) : sourceRecommendationOf(r))).filter(r => r.id);
  const base = { reportId: norm(input.reportId), reportRevision: norm(input.reportRevision), recommendations, assessment: input.assessment, documentary: { clientName: norm(input.documentary.clientName), projectName: norm(input.documentary.projectName), unitArea: norm(input.documentary.unitArea), osNumber: norm(input.documentary.osNumber), tag: norm(input.documentary.tag), equipmentType: norm(input.documentary.equipmentType), fabricationStandard: norm(input.documentary.fabricationStandard), inspectorName: norm(input.documentary.inspectorName) } };
  return { ...base, sourceHash: minutaSourceHash(base), generatedAt };
}

/** Fonte reconstruída de uma revisão gravada antes do hash existir (base para comparar sem "renascer" recomendações já incorporadas). */
export function sourceFromMinutaModel(model: any, reportId: string, reportRevision: string, generatedAt: string): MinutaSourceSnapshot {
  const recs: any[] = [];
  for (const g of model?.groups || []) for (const r of g.recommendations || []) recs.push({ ...r, discipline: r.discipline || g.discipline });
  const a = model?.assessment;
  return buildMinutaSource({
    reportId, reportRevision, recommendations: recs, fromMinutaModel: true,
    assessment: a ? { equipmentCondition: norm(a.equipmentCondition), equipmentResult: norm(a.equipmentResult), residualLifeMonths: a.residualLifeAdoptedMonths ?? null, residualLifeOrigin: norm(a.residualLifeOrigin) } : null,
    documentary: { clientName: model?.clientName || "", projectName: model?.projectName || "", unitArea: model?.unitArea || "", osNumber: model?.osNumber || "", tag: model?.tag || "", equipmentType: model?.equipmentType || "", fabricationStandard: model?.fabricationStandard || "", inspectorName: model?.inspectorName || "" }
  }, generatedAt);
}

export type MinutaSourceChangeType = "NOVA" | "ALTERADA" | "REMOVIDA" | "INALTERADA";

export interface MinutaSourceDiffItem {
  id: string;
  type: MinutaSourceChangeType;
  /** Campos alterados (só para ALTERADA). */
  fields: string[];
  current: MinutaSourceRecommendation | null;
  previous: MinutaSourceRecommendation | null;
}

export interface MinutaSourceDiff {
  updateNeeded: boolean;
  newCount: number;
  modifiedCount: number;
  removedCount: number;
  unchangedCount: number;
  /** NOVAS + ALTERADAS + REMOVIDAS + demais diferenças (avaliação/documentais). */
  pendingCount: number;
  otherChanges: string[];
  items: MinutaSourceDiffItem[];
}

const REC_FIELD_LABELS: Record<keyof MinutaSourceRecommendation, string> = { id: "ID", text: "Descritivo / Escopo", zr: "ZR", criticality: "Criticidade", discipline: "Disciplina", componentName: "Componente", location: "Localização", dimensions: "Dimensão", standard: "Norma", executionType: "Execução", status: "Status" };

/** Classifica cada Recommendation (id estável) como NOVA / ALTERADA / REMOVIDA / INALTERADA; sem fonte anterior nada é pendente. */
export function diffMinutaSource(previous: MinutaSourceSnapshot | null, current: MinutaSourceSnapshot): MinutaSourceDiff {
  if (!previous) return { updateNeeded: false, newCount: 0, modifiedCount: 0, removedCount: 0, unchangedCount: current.recommendations.length, pendingCount: 0, otherChanges: [], items: current.recommendations.map(r => ({ id: r.id, type: "INALTERADA" as const, fields: [], current: r, previous: null })) };
  const prevMap = new Map(previous.recommendations.map(r => [r.id, r]));
  const currMap = new Map(current.recommendations.map(r => [r.id, r]));
  const items: MinutaSourceDiffItem[] = [];
  for (const rec of current.recommendations) {
    const before = prevMap.get(rec.id);
    if (!before) { items.push({ id: rec.id, type: "NOVA", fields: [], current: rec, previous: null }); continue; }
    const fields = (Object.keys(REC_FIELD_LABELS) as Array<keyof MinutaSourceRecommendation>).filter(k => k !== "id" && before[k] !== rec[k]).map(k => REC_FIELD_LABELS[k]);
    items.push({ id: rec.id, type: fields.length > 0 ? "ALTERADA" : "INALTERADA", fields, current: rec, previous: before });
  }
  for (const rec of previous.recommendations) if (!currMap.has(rec.id)) items.push({ id: rec.id, type: "REMOVIDA", fields: [], current: null, previous: rec });
  const otherChanges: string[] = [];
  if (JSON.stringify(previous.assessment) !== JSON.stringify(current.assessment)) otherChanges.push("Avaliação de Engenharia (condição / Vida Residual)");
  if (JSON.stringify(previous.documentary) !== JSON.stringify(current.documentary)) otherChanges.push("Dados documentais");
  const newCount = items.filter(i => i.type === "NOVA").length;
  const modifiedCount = items.filter(i => i.type === "ALTERADA").length;
  const removedCount = items.filter(i => i.type === "REMOVIDA").length;
  const unchangedCount = items.filter(i => i.type === "INALTERADA").length;
  const hasChanges = newCount > 0 || modifiedCount > 0 || removedCount > 0 || otherChanges.length > 0;
  const updateNeeded = hasChanges;
  return { updateNeeded, newCount, modifiedCount, removedCount, unchangedCount, pendingCount: updateNeeded ? newCount + modifiedCount + removedCount + otherChanges.length : 0, otherChanges, items };
}
